/**
 * nfc-order-status.js — post-purchase lifecycle widget (Phase H3, issue #157;
 * master plan §14.4).
 *
 * Upgrades every element carrying [data-nfc-order-status][data-batch-id]:
 * fetches the authed App Proxy status endpoint and renders the stage badge,
 * a production timeline (Production → On its way → Delivered), and the CTAs
 * for that NFC line item. All requests go through the signed App Proxy
 * (/apps/studio/...) so the app verifies the Shopify signature and that the
 * logged-in customer owns the batch (IDOR enforced server-side).
 *
 * Fail-closed rules (§12.5): Revise / Request refund appear ONLY while the
 * server says stage === 'print_rejected'; on a 409 the widget shows
 * "status changed — refreshing" and re-fetches. If the proxy is unreachable
 * the widget degrades to a static "manage your design in Studio" link.
 *
 * Styling lives in assets/nfc-order-status.css (theme-token based). No
 * innerHTML with untrusted data — textContent / createElement only.
 */
(function () {
  'use strict';

  // Multi-app registry: each widget carries its own data-proxy-path (rendered
  // by nfc-order-line-status.liquid from the line item's _studio_app_key), so
  // batches minted by different apps each talk to their own backend.
  // Fail-safe proxy base via the shared resolver (#617) — one rule for the whole
  // theme, and never registry[0], which is `dev` on the production store.
  // Per-widget data-proxy-path (rendered from the line's _studio_app_key) wins.
  var DEFAULT_PROXY_BASE = (function () {
    if (typeof window.NfckeyResolveStudioApp === 'function') {
      var app = window.NfckeyResolveStudioApp(window.NfckeyStudioDefaultAppKey);
      return (app && app.proxyPath) || '';
    }
    var apps = Array.isArray(window.NfckeyStudioApps) ? window.NfckeyStudioApps : [];
    var keys = [window.NfckeyStudioDefaultAppKey, 'prod'];
    for (var k = 0; k < keys.length; k++) {
      if (!keys[k]) continue;
      for (var i = 0; i < apps.length; i++) {
        if (apps[i] && apps[i].key === keys[k] && apps[i].proxyPath) return apps[i].proxyPath;
      }
    }
    return '';
  })();

  var STAGE_LABEL = {
    draft: 'Draft',
    approved_unpaid: 'Awaiting payment',
    in_production: 'In production',
    print_rejected: 'Production issue — action needed',
    revision_hold: 'Revising',
    to_receive: 'On its way',
    completed: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
    archived: 'Archived'
  };

  // The happy-path production timeline (the special states render a banner
  // instead — an interruption should look like an interruption).
  var TIMELINE_STEPS = [
    { key: 'in_production', label: 'In production' },
    { key: 'to_receive', label: 'On its way' },
    { key: 'completed', label: 'Delivered' }
  ];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function badge(stage) {
    return el(
      'span',
      'nfc-os__badge nfc-os__badge--' + stage,
      STAGE_LABEL[stage] || stage
    );
  }

  function button(label, opts) {
    opts = opts || {};
    var cls = 'nfc-os-btn' + (opts.primary ? ' nfc-os-btn--primary' : '');
    var node = el(opts.href ? 'a' : 'button', cls, label);
    if (opts.href) {
      node.href = opts.href;
      if (opts.newTab) { node.target = '_blank'; node.rel = 'noopener'; }
    } else {
      node.type = opts.type || 'button';
    }
    if (opts.onClick) node.addEventListener('click', opts.onClick);
    return node;
  }

  function note(root, message, isError) {
    var msg = el('p', 'nfc-os__note' + (isError ? ' nfc-os__note--error' : ''), message);
    root.appendChild(msg);
    return msg;
  }

  function timeline(stage) {
    var reachedIndex = TIMELINE_STEPS.findIndex(function (s) { return s.key === stage; });
    if (reachedIndex === -1) return null;
    var wrap = el('div', 'nfc-os__timeline');
    TIMELINE_STEPS.forEach(function (step, i) {
      var state = '';
      if (i < reachedIndex || (stage === 'completed' && i === reachedIndex)) state = ' nfc-os__step--done';
      else if (i === reachedIndex) state = ' nfc-os__step--current';
      var stepEl = el('div', 'nfc-os__step' + state);
      stepEl.appendChild(el('span', 'nfc-os__step-dot'));
      stepEl.appendChild(el('span', 'nfc-os__step-label', step.label));
      wrap.appendChild(stepEl);
    });
    return wrap;
  }

  function studioFallback(root, proxyBase) {
    root.textContent = '';
    var wrap = el('div', 'nfc-os');
    var p = el('p', 'nfc-os__note', 'Live status is unavailable right now. ');
    var link = el('a', null, 'Manage your design in Studio');
    link.href = proxyBase;
    p.appendChild(link);
    wrap.appendChild(p);
    root.appendChild(wrap);
  }

  function postJson(url) {
    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        return { status: res.status, body: body };
      });
    });
  }

  function renderClaimForm(root, batchId, proxyBase) {
    var form = el('form', 'nfc-os__claim');
    form.appendChild(el('label', 'nfc-os__claim-label', 'What went wrong?'));
    var textarea = document.createElement('textarea');
    textarea.name = 'reason';
    textarea.required = true;
    textarea.maxLength = 2000;
    textarea.rows = 3;
    textarea.placeholder = 'Describe the problem — e.g. the print differs from the approved proof, or the item arrived damaged.';
    form.appendChild(textarea);

    form.appendChild(el('span', 'nfc-os__claim-hint',
      'Add photos of the problem (up to 3 — JPG, PNG or WebP, max 5 MB each):'));
    var fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.name = 'photos';
    fileInput.accept = 'image/jpeg,image/png,image/webp';
    fileInput.multiple = true;
    form.appendChild(fileInput);

    var actions = el('div', 'nfc-os__claim-actions');
    var submit = button('Submit claim', { primary: true, type: 'submit' });
    var cancel = button('Cancel', {
      onClick: function () { form.remove(); }
    });
    actions.appendChild(submit);
    actions.appendChild(cancel);
    form.appendChild(actions);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var files = Array.prototype.slice.call(fileInput.files || []);
      if (files.length > 3) {
        note(form, 'Please attach at most 3 photos.', true);
        return;
      }
      for (var i = 0; i < files.length; i += 1) {
        if (files[i].size > 5 * 1024 * 1024) {
          note(form, 'Each photo must be 5 MB or smaller.', true);
          return;
        }
      }
      submit.disabled = true;
      submit.textContent = 'Submitting…';
      var data = new FormData();
      data.set('reason', textarea.value);
      files.forEach(function (f) { data.append('photos', f); });
      fetch(proxyBase + '/api/shopify/claims/' + encodeURIComponent(batchId), { method: 'POST', body: data })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            return { status: res.status, body: body };
          });
        })
        .then(function (r) {
          if (r.status === 200) {
            form.textContent = '';
            form.appendChild(el('p', 'nfc-os__claim-label',
              'Claim received — it is now under review. We will contact you with the outcome.'));
          } else if (r.status === 409 && r.body.error === 'duplicate_claim') {
            form.textContent = '';
            form.appendChild(el('p', 'nfc-os__claim-label',
              'A claim for this item is already under review.'));
          } else if (r.status === 409 && r.body.error === 'window_closed') {
            form.textContent = '';
            form.appendChild(el('p', 'nfc-os__note nfc-os__note--error',
              'The 30-day claim window for this order has closed. Please contact support.'));
          } else {
            submit.disabled = false;
            submit.textContent = 'Submit claim';
            note(form, 'Could not submit the claim. Please try again or contact support.', true);
          }
        })
        .catch(function () {
          submit.disabled = false;
          submit.textContent = 'Submit claim';
          note(form, 'Could not submit the claim. Please try again.', true);
        });
    });

    root.appendChild(form);
  }

  function render(root, batchId, data, refresh, proxyBase) {
    root.textContent = '';
    var box = el('div', 'nfc-os');
    root.appendChild(box);

    var stage = data.stage;
    var proofHref = data.proof_url ? proxyBase + data.proof_url : null;
    var editorHref = data.project_id
      ? proxyBase + '?project_id=' + encodeURIComponent(data.project_id)
      : proxyBase;

    box.appendChild(badge(stage));

    var line = timeline(stage);
    if (line) box.appendChild(line);

    function staleThenRefresh() {
      note(box, "This order's status changed — refreshing…", true);
      setTimeout(refresh, 1200);
    }

    if (stage === 'print_rejected') {
      // Mirrors PRODUCTION_ISSUE_COPY / EditorLockBanner in src/ (#371/#372) —
      // never asserts a printing cause; the admin's own reason explains it.
      var bannerText = data.print_rejected_reason
        ? '"' + data.print_rejected_reason + '" — revise it, or request a full refund.'
        : "We can't produce this order as it is — revise it, or request a full refund.";
      var banner = el('div', 'nfc-os__banner nfc-os__banner--alert', bannerText);
      box.appendChild(banner);
    }
    if (stage === 'revision_hold') {
      box.appendChild(el('div', 'nfc-os__banner nfc-os__banner--info',
        'This design is reopened for changes. Resume in the editor, then re-approve the new proof to continue production.'));
    }

    var row = el('div', 'nfc-os__row');
    box.appendChild(row);

    if (proofHref) {
      row.appendChild(button('View proof', { href: proofHref, newTab: true }));
    }

    if (stage === 'print_rejected') {
      row.appendChild(button('Revise design', {
        primary: true,
        onClick: function (event) {
          event.preventDefault();
          postJson(proxyBase + '/api/batches/' + encodeURIComponent(batchId) + '/print-issue/revise').then(function (r) {
            if (r.status === 200) {
              window.location.href = editorHref;
            } else if (r.status === 409) {
              staleThenRefresh();
            } else {
              note(box, 'Could not start the revision. Please refresh and try again.', true);
            }
          }).catch(function () {
            note(box, 'Could not start the revision. Please refresh and try again.', true);
          });
        }
      }));
      row.appendChild(button('Request refund (full)', {
        onClick: function (event) {
          event.preventDefault();
          if (!window.confirm('Request a full refund for this item? This cannot be undone.')) return;
          postJson(proxyBase + '/api/batches/' + encodeURIComponent(batchId) + '/print-issue/refund').then(function (r) {
            if (r.status === 200) {
              refresh();
            } else if (r.status === 409) {
              staleThenRefresh();
            } else {
              note(box, 'Could not request the refund. Please refresh and try again.', true);
            }
          }).catch(function () {
            note(box, 'Could not request the refund. Please refresh and try again.', true);
          });
        }
      }));
    }

    if (stage === 'revision_hold') {
      row.appendChild(button('Resume in editor', { primary: true, href: editorHref }));
    }

    if (stage === 'to_receive' || stage === 'completed' || stage === 'refunded') {
      row.appendChild(button('Reorder', {
        onClick: function (event) {
          event.preventDefault();
          var btn = event.currentTarget;
          btn.disabled = true;
          btn.textContent = 'Creating…';
          postJson(proxyBase + '/api/shopify/reorder/' + encodeURIComponent(batchId)).then(function (r) {
            if (r.status === 200 && r.body.editor_url) {
              window.location.href = r.body.editor_url;
            } else if (r.status === 409) {
              staleThenRefresh();
            } else {
              btn.disabled = false;
              btn.textContent = 'Reorder';
              note(box, 'Could not start the reorder. Please try again.', true);
            }
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = 'Reorder';
            note(box, 'Could not start the reorder. Please try again.', true);
          });
        }
      }));
    }

    if (stage === 'completed') {
      row.appendChild(button('Report a problem', {
        onClick: function (event) {
          event.preventDefault();
          if (box.querySelector('form')) return;
          renderClaimForm(box, batchId, proxyBase);
        }
      }));
    }
  }

  function init(root) {
    if (root.dataset.nfcStatusInit === '1') return;
    root.dataset.nfcStatusInit = '1';
    var batchId = root.dataset.batchId;
    if (!batchId) return;
    var proxyBase = root.dataset.proxyPath || DEFAULT_PROXY_BASE;

    function refresh() {
      fetch(proxyBase + '/api/shopify/order-status/' + encodeURIComponent(batchId), { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (res.status === 401 || res.status === 403) {
            root.textContent = '';
            var wrap = el('div', 'nfc-os');
            wrap.appendChild(el('p', 'nfc-os__note',
              'Log in with the account that placed this order to see its design status.'));
            root.appendChild(wrap);
            return null;
          }
          if (!res.ok) throw new Error('status ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (data && data.stage) render(root, batchId, data, refresh, proxyBase);
        })
        .catch(function () {
          studioFallback(root, proxyBase);
        });
    }

    root.textContent = '';
    var loading = el('div', 'nfc-os');
    loading.appendChild(el('p', 'nfc-os__note', 'Checking design status…'));
    root.appendChild(loading);
    refresh();
  }

  function scan() {
    document.querySelectorAll('[data-nfc-order-status][data-batch-id]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
